import { z } from 'zod'

// Before the schemas below: this module can load ahead of main.tsx's own import of it.
import '@/shared/config/zod-config'

import { ApiError, readProblem } from '@/shared/api/api-error'
import type { ApiClient } from '@/shared/api/client'
import { log, LogLevel } from '@/shared/logging'

/** Site Management's sign-in session endpoints. */
const sessionsPath = '/site-management/v1/sessions'
const currentSessionPath = `${sessionsPath}/current`
const emailCodePath = `${sessionsPath}/email-code`
const idleTimePath = `${currentSessionPath}/idle-time`

const sessionSchema = z.object({
  userProfileId: z.string().min(1),
  organizationProfileId: z.string().min(1),
  siteId: z.string().min(1),
  firstName: z.string(),
  lastName: z.string(),
  /** An internal path, sanitized before use like any return path. */
  landingPage: z.string().nullish(),
  pendingPrompts: z.array(z.object({ kind: z.string(), fields: z.array(z.string()) })),
  /** The real person's own profile while they impersonate `userProfileId`, whose name the session carries (AC-12). */
  impersonatorUserProfileId: z.string().nullish(),
  /** The site a Super User chose to act on in place of `siteId`, the address's own, and its name (110552). */
  actingSiteId: z.string().nullish(),
  actingSiteName: z.string().nullish(),
})

/** Who is signed in on this site, as the API's session says. */
export type SignedInSession = z.infer<typeof sessionSchema>

const answerSchema = z.discriminatedUnion('status', [
  // `destination`: where a one-time sign-in link goes (SM-39), sanitized like any return path.
  z.object({ status: z.literal('signedIn'), session: sessionSchema, destination: z.string().nullish() }),
  z.object({ status: z.literal('codeSent'), maskedEmail: z.string().min(1) }),
])

/** The answer to a sign-in: a session, or a code emailed to a masked address to enter first. */
export type SignInAnswer = z.infer<typeof answerSchema>

const idleTimeSchema = z.object({ secondsLeft: z.number().int().min(0), idleTimeoutSeconds: z.number().int().positive() })

/** How long the session lasts without another request, as the API read it without renewing it. */
export type SessionIdleTime = z.infer<typeof idleTimeSchema>

/**
 * The calls behind the session. A refusal rejects with an {@link ApiError} carrying its `code`; no
 * answer by the call's deadline rejects with an {@link ApiError} of status 0.
 */
export interface SessionClient {
  /**
   * The session the cookie holds, or `null` when there is none. `isSilent`: for a check the app
   * makes on its own, whose token never starts a sign-in; one that needs it rejects.
   */
  readonly current: (signal?: AbortSignal, options?: { readonly isSilent?: boolean }) => Promise<SignedInSession | null>
  /** Signs in after B2C; called again while a code is awaited, it sends a new one. */
  readonly start: () => Promise<SignInAnswer>
  /** Finishes a sign-in with the emailed code. */
  readonly confirmCode: (code: string) => Promise<SignInAnswer>
  /** Ends the session and clears its cookie. */
  readonly end: () => Promise<void>
  /** How long the session has left, read without starting its idle time again; `null` when it has ended. */
  readonly idleTimeLeft: (signal?: AbortSignal) => Promise<SessionIdleTime | null>
}

/**
 * How long a call may take before sign-in stops waiting and says TMS cannot be reached. Starting a
 * session can email a code, so it gets longer than a read. Sign-out stops waiting to end the session
 * and goes on to B2C, so a hung request never holds the person signed in. An object, so tests can
 * shorten it.
 */
export const sessionDeadlines = { readMs: 10_000, signInMs: 20_000, endMs: 10_000 }

/**
 * Runs a call that gives up at its deadline, rejecting with an {@link ApiError} of status 0, as
 * when no answer comes at all. Aborting `given` rejects at once as an abort.
 */
async function withDeadline<T>(
  name: string,
  ms: number,
  call: (signal: AbortSignal) => Promise<T>,
  given?: AbortSignal,
): Promise<T> {
  if (given?.aborted === true) {
    throw aborted()
  }
  const controller = new AbortController()
  let deadline: ReturnType<typeof setTimeout> | undefined
  let release: (() => void) | undefined
  // Settles the call at its deadline or the caller's abort, even while a step that ignores the
  // signal, such as getting a token, still waits.
  const stopped = new Promise<never>((_, reject) => {
    deadline = setTimeout(() => {
      log(LogLevel.Warning, `${name} did not answer in time.`, { deadlineMs: ms })
      reject(new ApiError(`${name} did not answer in time.`, 0))
      controller.abort()
    }, ms)
    const forward = () => {
      clearTimeout(deadline)
      reject(aborted())
      controller.abort()
    }
    given?.addEventListener('abort', forward)
    release = () => given?.removeEventListener('abort', forward)
  })
  try {
    return await Promise.race([call(controller.signal), stopped])
  } finally {
    clearTimeout(deadline)
    release?.()
  }
}

function aborted(): DOMException {
  return new DOMException('The call was aborted.', 'AbortError')
}

/**
 * The session calls through the API client, which sends the token and the site header. `probe`
 * carries the idle check, which the app makes on its own: one whose token never starts a sign-in.
 */
export function createSessionClient(client: ApiClient, probe: ApiClient = client): SessionClient {
  return {
    current(given, options) {
      const reader = options?.isSilent === true ? probe : client
      return withDeadline(`GET ${currentSessionPath}`, sessionDeadlines.readMs, (signal) => readCurrent(reader, signal), given)
    },

    start() {
      return withDeadline(`POST ${sessionsPath}`, sessionDeadlines.signInMs, async (signal) =>
        answerSchema.parse(await client.post(sessionsPath, undefined, { signal })),
      )
    },

    confirmCode(code) {
      return withDeadline(`POST ${emailCodePath}`, sessionDeadlines.signInMs, async (signal) =>
        answerSchema.parse(await client.post(emailCodePath, { code }, { signal })),
      )
    },

    // Through the probe: signing out never starts a B2C sign-in first. A token that cannot renew
    // silently leaves the session to end on its own.
    end() {
      return withDeadline(`DELETE ${currentSessionPath}`, sessionDeadlines.endMs, (signal) =>
        probe.delete(currentSessionPath, { ifMatch: null, signal }),
      )
    },

    idleTimeLeft(given) {
      return withDeadline(`GET ${idleTimePath}`, sessionDeadlines.readMs, (signal) => readIdleTime(probe, signal), given)
    },
  }
}

async function readIdleTime(client: ApiClient, signal: AbortSignal): Promise<SessionIdleTime | null> {
  const response = await client.getWithStatus(idleTimePath, signal)
  if (response.status === 401) {
    return null
  }
  if (response.status !== 200) {
    throw new ApiError(`GET ${idleTimePath} responded ${String(response.status)}.`, response.status, {
      problem: readProblem(response.body),
    })
  }
  return idleTimeSchema.parse(response.body)
}

async function readCurrent(client: ApiClient, signal: AbortSignal): Promise<SignedInSession | null> {
  const response = await client.getWithStatus(currentSessionPath, signal)
  if (response.status === 401) {
    return null
  }
  if (response.status !== 200) {
    throw new ApiError(`GET ${currentSessionPath} responded ${String(response.status)}.`, response.status, {
      problem: readProblem(response.body),
    })
  }
  return sessionSchema.parse(response.body)
}
